import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import api from "../../utils/api";

function formatNaira(n) {
  const num = Number(n || 0);
  return `₦${num.toLocaleString()}`;
}

export default function AgentFinancePage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [busyWithdraw, setBusyWithdraw] = useState(false);

  // 🔔 Toast state
  const [toast, setToast] = useState({
    open: false,
    severity: "info",
    message: "",
  });

  const [finance, setFinance] = useState({
    available: 0,
    paid_total: 0,
    earned_total: 0,
    min_payout: 0,
    payout_ready: false,
    has_pending_request: false,
    payout_verified: false,
  });

  // ===============================
  // LOAD FINANCE SUMMARY
  // ===============================
  const loadFinance = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/agent/finance");

      setFinance({
        available: Number(res.data?.available || 0),
        paid_total: Number(res.data?.paid_total || 0),
        earned_total: Number(res.data?.earned_total || 0),
        min_payout: Number(res.data?.min_payout || 0),
        payout_ready: Boolean(res.data?.payout_ready),
        has_pending_request: Boolean(res.data?.has_pending_request),
        payout_verified: Boolean(res.data?.payout_verified),
      });
    } catch (err) {
      console.error("Failed to load agent finance", err);
      setToast({
        open: true,
        severity: "error",
        message: "Failed to load finance summary.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ Load ONCE on page mount (NO AUTO-REFRESH)
  useEffect(() => {
    loadFinance();
  }, [loadFinance]);

  // ===============================
  // SINGLE SOURCE OF TRUTH
  // ===============================
  const canWithdraw =
    finance.payout_verified &&
    !finance.has_pending_request &&
    finance.available >= finance.min_payout;

  // ===============================
  // WITHDRAW ALL
  // ===============================
  const withdrawAll = async () => {
    if (!canWithdraw || busyWithdraw) return;

    const ok = window.confirm(
      `Withdraw ALL available commission (${formatNaira(finance.available)})?`
    );
    if (!ok) return;

    try {
      setBusyWithdraw(true);
      await api.post("/agent/payouts/request");

      setToast({
        open: true,
        severity: "success",
        message: "Payout request submitted successfully.",
      });

      // 🔄 Manual refresh ONLY (kept)
      await loadFinance();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Failed to request payout. Please try again.";

      setToast({
        open: true,
        severity: "error",
        message: msg,
      });
    } finally {
      setBusyWithdraw(false);
    }
  };

  // ===============================
  // PAYOUT STATUS CHIP
  // ===============================
  const payoutStatusChip = () => {
    if (!finance.payout_verified) {
      return <Chip label="Bank Not Verified" color="error" size="small" />;
    }
    if (finance.has_pending_request) {
      return (
        <Chip label="Pending payout request" color="warning" size="small" />
      );
    }
    if (canWithdraw) {
      return <Chip label="Payout Ready" color="success" size="small" />;
    }
    return <Chip label="Not Ready" size="small" />;
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} gutterBottom>
        Agent Finance
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Track your commission balance and request payouts.
      </Typography>

      {loading ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress />
        </Paper>
      ) : (
        <>
          {/* ================= KPIs ================= */}
          <Grid container spacing={3}>
            <KpiCard
              title="Available Commission"
              value={formatNaira(finance.available)}
              icon={<AccountBalanceWalletIcon />}
              subtitle="Earned but not yet paid"
            />
            <KpiCard
              title="Paid Total"
              value={formatNaira(finance.paid_total)}
              icon={<DoneAllIcon />}
              subtitle="Already paid out"
            />
            <KpiCard
              title="Earned Total"
              value={formatNaira(finance.earned_total)}
              icon={<PaymentsIcon />}
              subtitle="Lifetime earnings"
            />
          </Grid>

          {/* ================= PAYOUT BOX ================= */}
          <Paper sx={{ mt: 3, p: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { md: "center" },
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box>
                <Typography fontWeight={800} mb={0.5}>
                  Payout Request
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Minimum payout: {formatNaira(finance.min_payout)}
                </Typography>

                <Box sx={{ mt: 1 }}>{payoutStatusChip()}</Box>
              </Box>

              {/* ===== ACTION BUTTONS ===== */}
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={withdrawAll}
                  disabled={!canWithdraw || busyWithdraw}
                  startIcon={
                    busyWithdraw ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <AccountBalanceWalletIcon />
                    )
                  }
                >
                  {busyWithdraw ? "Requesting..." : "Withdraw All Available"}
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate("/agent/payout-history")}
                >
                  Go to Payout History
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* ================= INFO ================= */}
            {!finance.payout_verified ? (
              <Alert severity="error">
                Your bank details are not verified.
              </Alert>
            ) : finance.has_pending_request ? (
              <Alert severity="info">
                You have a pending payout request. Once rejected or processed,
                you can request again.
              </Alert>
            ) : finance.available < finance.min_payout ? (
              <Alert severity="warning">
                Minimum payout is {formatNaira(finance.min_payout)}. Available:{" "}
                <b>{formatNaira(finance.available)}</b>.
              </Alert>
            ) : (
              <Alert severity="success">
                You are eligible to request payout now.
              </Alert>
            )}
          </Paper>
        </>
      )}

      {/* ================= TOAST ================= */}
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ==========================
   KPI CARD
========================== */
function KpiCard({ title, value, subtitle, icon }) {
  return (
    <Grid item xs={12} md={4}>
      <Paper sx={{ p: 3, display: "flex", gap: 2, alignItems: "center" }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: "rgba(25,118,210,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "primary.main",
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography fontWeight={900} sx={{ fontSize: 20 }}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
      </Paper>
    </Grid>
  );
}
